import React, { useEffect, useState } from 'react';

export interface AlertProps {
  color?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';
  variant?: 'solid' | 'outline' | 'soft';
  isOpen?: boolean;
  toggle?: () => void;
  className?: string;
  children: React.ReactNode;
}

export const Alert: React.FC<AlertProps> = ({
  color = 'primary',
  variant = 'solid',
  isOpen = true,
  toggle,
  className = '',
  children,
  ...props
}) => {
  const [mounted, setMounted] = useState(isOpen);

  useEffect(() => {
    setMounted(isOpen);
  }, [isOpen]);

  if (!mounted) return null;

  const getColorVars = (c: string) => {
    switch (c) {
      case 'secondary': return { base: 'var(--color-gray-600)', bg: 'var(--color-gray-600)' };
      case 'light': return { base: 'var(--color-light)', bg: 'var(--color-light)' };
      case 'warning': return { base: 'var(--color-warning)', bg: 'var(--color-warning)' };
      case 'dark': return { base: 'var(--color-dark)', bg: 'var(--color-dark)' };
      default: return { base: `var(--color-${c})`, bg: `var(--color-${c})` };
    }
  };

  const { base: colorVar } = getColorVars(color);
  
  const isLight = color === 'light';
  const isWarning = color === 'warning';
  
  const solidTextColor = (isLight || isWarning) ? (isLight ? 'text-[var(--color-dark)]' : 'text-gray-900') : 'text-white';
  const coloredTextColor = isLight ? 'text-[var(--color-dark)]' : `text-[${colorVar}]`;

  let variantClasses = '';
  
  switch (variant) {
    case 'solid':
      variantClasses = `bg-[${colorVar}] ${solidTextColor} border-[${colorVar}]`;
      break;
    case 'outline':
      variantClasses = `bg-transparent border-[${colorVar}] ${coloredTextColor} border`;
      break;
    case 'soft':
      variantClasses = `bg-[${colorVar}] bg-opacity-10 ${coloredTextColor} border-transparent`;
      break;
  }

  const handleDismiss = () => {
    if (toggle) {
      toggle();
    }
  };

  return (
    <div 
      className={`${variantClasses} rounded-lg p-4 mb-4 flex items-start ${className || ''}`}
      role="alert"
      {...props}
    >
      <div className="flex-1">{children}</div>
      {toggle && (
        <button 
          onClick={handleDismiss}
          className={`ml-4 ${variant === 'solid' ? 'text-white opacity-70 hover:opacity-100' : 'text-current opacity-50 hover:opacity-100'}`}
          aria-label="Close"
        >
          ✕
        </button>
      )}
    </div>
  );
};
